
import {Injectable, Injector, inject} from '@angular/core';
import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest
} from '@angular/common/http';
import {Observable} from 'rxjs';

import {AuthenticationService} from '../_services/authentication.service';
import {ApiEndpoints} from '../_constants/api-endpoints';
import {AppConfig} from '../_data/config/app-config';
import {JWT_TYPE_HEADER} from '../_constants/constants';

/**
 * Automatically injects the authentication header into all requests to the backend
 */
@Injectable()
export class JwtInterceptor implements HttpInterceptor {

  private readonly injector = inject(Injector);

  private readonly appConfig = inject(AppConfig);
  private readonly apiEndpoints = inject(ApiEndpoints);

  /**
   * Lazily resolve AuthenticationService ONLY when needed
   * This breaks the HttpClient ↔ interceptor ↔ auth service cycle
   */
  private get authenticationService(): AuthenticationService {
    return this.injector.get(AuthenticationService);
  }

  private get jwtTokenPair() {
    return this.authenticationService.currentJwtTokenPair;
  }

  private isInterceptable(request: HttpRequest<any>): boolean {
    const isAdminUrl = request.url.startsWith(this.appConfig.adminServer);
    const isReportsUrl = request.url.startsWith(this.appConfig.reportsServer);
    const isTokenUrl = request.url.startsWith(this.apiEndpoints.securityAuthenticate) || request.url.startsWith(this.apiEndpoints.securityRefreshToken);

    return (
      // If either token exists
      (this.jwtTokenPair?.full || this.jwtTokenPair?.refresh) &&
      // ...and it's an Admin or Reports URL
      (isAdminUrl || isReportsUrl) &&
      // ...and exclude token URLs
      !isTokenUrl
    );
  }

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {

    if(this.isInterceptable(request)) {

      // Exclude requests that already have a Bearer token
      // Or if a service specifically says to never send the token, don't.
      if(
        !request.headers.get('Authorization')?.startsWith('Bearer') &&
        request.headers.get(JWT_TYPE_HEADER) !== 'none'
      ) {
        // console.debug(
        //   `Injecting authorization header for "${request.url}".  Incoming Headers: (${JSON.stringify(request.headers.keys().map(k => `${k} => ${request.headers.get(k)}`))})`
        // );

        //If a service sets a Jwt-Type header to either auth or refresh, use that to force the appropriate token.
        //Default is to use either token, preferring the refresh.

        let bearerToken =
          this.jwtTokenPair.refresh?.token ??
          this.jwtTokenPair.full?.token;

        if(request.headers.get(JWT_TYPE_HEADER) === 'auth') {
          bearerToken = this.jwtTokenPair.full?.token;
        } else if(request.headers?.get(JWT_TYPE_HEADER) === 'refresh') {
          console.debug('Forcing refresh token');
          bearerToken = this.jwtTokenPair.refresh?.token;
        }

        request = request.clone({
          setHeaders: {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            Authorization: `Bearer ${bearerToken}`
          }
        });
      }

      // service resolved lazily AFTER HttpClient setup
      if(this.jwtTokenPair.refresh) {
        console.debug('JWT Interceptor refreshing session');
        this.authenticationService.refreshSession();
      }
    }

    return next.handle(request);
  }
}
